import React from "react";
import { withRouter } from "react-router-dom";
import Loader from "../../component/common/Loader";
import AparEmployeeHeader, { normalizeManagers, mergeManagerData } from "../../component/common/AparEmployeeHeader";
import CONSTANT from "../../constant";
import { getKpiReport, approveKpiReport, sendBackKpiReport, getSendBackHistory } from "../../actions/AppraisalCycleAction";
import { getAuthToken, formatDateTimeDDMMYY } from "../../utils";

class KpiApproval extends React.Component<any, any> {
  constructor(props: any) {
    super(props);

    const locationState = props.location?.state || {};

    this.state = {
      showLoader: false,
      role: locationState.role || 'reporting',
      employeeCode: locationState.employeeCode || '',
      employeeName: locationState.employeeName || '',
      employeeNumber: locationState.employeeNumber || '',
      effectiveFrom: locationState.effectiveFrom || '',
      effectiveTo: locationState.effectiveTo || '',
      ...normalizeManagers(locationState),
      kpiReportId: null,
      kpiStatus: null,
      isMouApplicable: false,
      mouTask: null,
      otherTasks: [],
      sendBackRemarks: null,
      sendBackHistory: [],
      showSendBackModal: false,
      sendBackRemarksInput: '',
      uploadedFileUrl: null,
      uploadedFileName: null,
      apiError: null,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const roleName = this.state.role === 'reporting' ? 'Reporting Manager' : 'Reviewing Manager';
    document.title = `KPI Approval - ${roleName} - ` + CONSTANT.organisation.name;

    if (!this.state.employeeCode) {
      this.goBack();
      return;
    }

    this.fetchKpiReport();
  }

  fetchKpiReport = () => {
    const { employeeCode, effectiveFrom } = this.state;
    this.setState({ showLoader: true });
    getKpiReport({ employeeCode, effectiveFrom, reportType: 'employee' }).then((res: any) => {
      if (res?.status === 'failed' || res?.error) {
        this.setState({ showLoader: false, apiError: res?.error || 'Failed to load KPI report.' });
        return;
      }
      const report = res?.result || null;
      if (report) {
        const mouTask = report.tasks?.find((t: any) => t.particular === '*)') || null;
        const otherTasks = report.tasks?.filter((t: any) => t.particular !== '*)') || [];

        const uploadedFileUrl = report.uploaded_file
          ? `${process.env.REACT_APP_BASE_URL}apar/kpi-report/get-file?kpiReportId=${report.id}&filename=${report.uploaded_file}`
          : null;

        this.setState((prev: any) => ({
          kpiReportId: report.id,
          kpiStatus: report.kpi_status || 'draft',
          isMouApplicable: report.is_mou_applicable || false,
          mouTask,
          otherTasks,
          sendBackRemarks: report.sendBackRemarks || null,
          sendBackHistory: report.sendBackHistory || [],
          uploadedFileUrl,
          uploadedFileName: report.uploaded_file_original_name || report.uploaded_file || null,
          apiError: null,
          ...mergeManagerData(prev, report),
        }));
      }
      this.setState({ showLoader: false });
    }).catch(() => {
      this.setState({ showLoader: false, apiError: 'Failed to load KPI report. Please try again.' });
    });
  };

  handleApprove = () => {
    const { kpiReportId, role } = this.state;
    if (!kpiReportId) return;

    const roleName = role === 'reporting' ? 'Reporting Manager' : 'Reviewing Manager';
    if (!window.confirm(`Are you sure you want to approve this KPI report as ${roleName}?`)) return;

    this.setState({ showLoader: true });
    approveKpiReport({ kpiReportId, role }).then((res: any) => {
      if (res?.result) {
        const newStatus = role === 'reporting' ? 'rm_approved' : 'rvm_approved';
        this.setState({ kpiStatus: newStatus, showLoader: false });
        alert('KPI report approved successfully.');
      } else {
        this.setState({ showLoader: false });
      }
    }).catch(() => {
      this.setState({ showLoader: false });
    });
  };

  goBack = () => {
    const { role } = this.state;
    const url = role === 'reporting'
      ? CONSTANT.component.reportingManagerReport.url
      : CONSTANT.component.reviewingManagerReport.url;
    this.props.history.push(url);
  };

  handleSendBack = () => {
    const { kpiReportId, role, sendBackRemarksInput } = this.state;
    if (!kpiReportId || !sendBackRemarksInput.trim()) {
      alert('Please enter remarks explaining why you are sending back.');
      return;
    }

    const roleName = role === 'reporting' ? 'Reporting Manager' : 'Reviewing Manager';
    if (!window.confirm(`Are you sure you want to send back this KPI report as ${roleName}?`)) return;

    this.setState({ showLoader: true });
    sendBackKpiReport({
      kpiReportId,
      sendBackBy: role,
      remarks: sendBackRemarksInput.trim(),
    }).then((res: any) => {
      if (res?.kpi_status || res?.result) {
        const newStatus = role === 'reporting' ? 'sent_back_by_rm' : 'sent_back_by_rvm';
        this.setState({
          kpiStatus: newStatus,
          showLoader: false,
          showSendBackModal: false,
          sendBackRemarksInput: '',
        });
        alert('KPI report has been sent back successfully.');
      } else {
        this.setState({ showLoader: false });
      }
    }).catch(() => {
      this.setState({ showLoader: false });
    });
  };

  fetchAuthBlob = (url: string): Promise<Blob> => {
    const token = getAuthToken();
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then((res) => res.blob());
  };

  openUploadedFile = () => {
    const { uploadedFileUrl } = this.state;
    if (!uploadedFileUrl) return;
    this.fetchAuthBlob(uploadedFileUrl)
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      })
      .catch(() => alert('Failed to open file.'));
  };

  downloadUploadedFile = () => {
    const { uploadedFileUrl, uploadedFileName } = this.state;
    if (!uploadedFileUrl) return;
    this.fetchAuthBlob(uploadedFileUrl)
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = uploadedFileName || 'document';
        a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      })
      .catch(() => alert('Failed to download file.'));
  };

  fetchSendBackHistory = () => {
    const { kpiReportId } = this.state;
    if (!kpiReportId) return;
    getSendBackHistory({ kpiReportId }).then((res: any) => {
      this.setState({ sendBackHistory: res?.result || res || [] });
    });
  };

  canApprove = () => {
    const { role, kpiStatus } = this.state;
    if (role === 'reporting' && (kpiStatus === 'submitted' || kpiStatus === 'sent_back_by_rvm')) return true;
    if (role === 'reviewing' && kpiStatus === 'rm_approved') return true;
    return false;
  };

  canSendBack = () => {
    const { role, kpiStatus } = this.state;
    if (role === 'reporting' && (kpiStatus === 'submitted' || kpiStatus === 'sent_back_by_rvm')) return true;
    if (role === 'reviewing' && kpiStatus === 'rm_approved') return true;
    return false;
  };

  getStatusBadge = (status: string) => {
    const map: any = {
      draft: { cls: 'badge-secondary', label: 'Draft' },
      submitted: { cls: 'badge-warning', label: 'Submitted - Pending RM Approval' },
      rm_approved: { cls: 'badge-info', label: 'RM Approved - Pending RVM Approval' },
      rvm_approved: { cls: 'badge-success', label: 'Fully Approved' },
      sent_back_by_rm: { cls: 'badge-danger', label: 'Sent Back by Reporting Manager' },
      sent_back_by_rvm: { cls: 'badge-danger', label: 'Sent Back by Reviewing Manager' },
    };
    const info = map[status] || { cls: 'badge-secondary', label: status };
    return <span className={`badge ${info.cls}`}>{info.label}</span>;
  };

  render() {
    const {
      showLoader, role, employeeName, employeeNumber,
      effectiveFrom, effectiveTo,
      reportingManager, reviewingManager, acceptingAuthority,
      kpiReportId, kpiStatus, isMouApplicable, mouTask, otherTasks,
      sendBackRemarks, sendBackHistory, showSendBackModal, sendBackRemarksInput,
      uploadedFileUrl, uploadedFileName, apiError,
    } = this.state;

    const roleName = role === 'reporting' ? 'Reporting Manager' : 'Reviewing Manager';
    const canApprove = this.canApprove();
    const canSendBack = this.canSendBack();

    return (
      <div className="container mx-auto px-4 py-3">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">

                <div className="row mb-3">
                  <div className="col-md-8">
                    <h1 className="page-title">KPI Report Review - {roleName}</h1>
                    {kpiStatus && (
                      <p className="mt-1 mb-0">Status: {this.getStatusBadge(kpiStatus)}</p>
                    )}
                  </div>
                  <div className="col-md-4 text-right">
                    <button
                      type="button"
                      className="btn btn-sm cancel-button"
                      onClick={this.goBack}
                    >
                      <i className="fa fa-arrow-left mr-2" />Back to Employees
                    </button>
                  </div>
                </div>
                <AparEmployeeHeader
                  employeeName={employeeName}
                  employeeNumber={employeeNumber}
                  effectiveFrom={effectiveFrom}
                  effectiveTo={effectiveTo}
                  reportingManager={reportingManager}
                  reviewingManager={reviewingManager}
                  acceptingAuthority={acceptingAuthority}
                />

                {showLoader && <Loader />}

                {!showLoader && apiError && (
                  <div className="alert alert-danger">
                    <i className="fa fa-exclamation-circle mr-2" />
                    {apiError}
                  </div>
                )}

                {!showLoader && !apiError && !kpiReportId && (
                  <div className="alert alert-warning">
                    This employee has not submitted a KPI report yet.
                  </div>
                )}

                {/* Send-back banner for RM when RvM sent it back */}
                {kpiStatus === 'sent_back_by_rvm' && role === 'reporting' && sendBackRemarks && (
                  <div className="alert alert-danger mt-2">
                    <strong><i className="fa fa-exclamation-triangle mr-1" /> Sent Back by Reviewing Manager:</strong>{' '}
                    {sendBackRemarks.remarks}
                    <div className="text-muted small mt-1">
                      {sendBackRemarks.created_at && `On: ${formatDateTimeDDMMYY(sendBackRemarks.created_at)}`}
                    </div>
                  </div>
                )}

                {/* Send-back history */}
                {sendBackHistory.length > 0 && (
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => this.setState({ showHistory: !this.state.showHistory })}
                    >
                      <i className="fa fa-history mr-1" />
                      {this.state.showHistory ? 'Hide' : 'View'} Send-Back History ({sendBackHistory.length})
                    </button>
                    {this.state.showHistory && (
                      <div className="mt-2">
                        {sendBackHistory.map((item: any, i: number) => (
                          <div key={item.id || i} className="alert alert-light border py-2 mb-1">
                            <strong>{item.send_back_by === 'reporting' ? 'Reporting Manager' : 'Reviewing Manager'}</strong>
                            {' sent back to '}
                            <strong>{item.send_back_to === 'employee' ? 'Employee' : 'Reporting Manager'}</strong>
                            <span className="text-muted small ml-2">
                              {item.created_at && `(${formatDateTimeDDMMYY(item.created_at)})`}
                            </span>
                            <div className="mt-1">{item.remarks}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!showLoader && kpiReportId && (
                  <div>
                    <h2 className="fs-5 fw-bold border-bottom pb-2 mb-4">
                      Annual Work Plan
                    </h2>

                    <div className="table-responsive mb-2">
                      <table className="table table-bordered table-sm align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '5%' }}>#</th>
                            <th style={{ width: '35%' }}>Tasks to be Performed</th>
                            <th style={{ width: '10%' }}>Weightage</th>
                            <th style={{ width: '25%' }}>Deliverables</th>
                          </tr>
                        </thead>
                        <tbody>

                          {/* MOU Section */}
                          <tr className="table-secondary">
                            <td colSpan={4} className="fw-semibold">
                              I – MoU Targets
                              {isMouApplicable && mouTask && (
                                <span className="text-muted fw-normal ms-2 small">(Weightage: {mouTask.weightage || 20}%)</span>
                              )}
                            </td>
                          </tr>

                          {isMouApplicable && mouTask ? (
                            <tr>
                              <td className="text-center fw-semibold text-muted">*)</td>
                              <td>{mouTask.task_description || '-'}</td>
                              <td className="text-center">{mouTask.weightage || '-'}</td>
                              <td>{mouTask.deliverables || '-'}</td>
                            </tr>
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-muted fst-italic ps-3 small">MoU not applicable</td>
                            </tr>
                          )}

                          {/* Other Tasks Section */}
                          <tr className="table-secondary">
                            <td colSpan={4} className="fw-semibold">
                              II – Other Key Assigned Tasks flowing from KPI
                              <span className="text-muted fw-normal ms-2 small">(Weightage: {isMouApplicable ? 55 : 75}%)</span>
                            </td>
                          </tr>

                          {otherTasks.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-muted fst-italic ps-3 small">No tasks.</td>
                            </tr>
                          ) : (
                            otherTasks.map((task: any, index: number) => (
                              <tr key={task.id || index}>
                                <td className="text-center fw-semibold text-muted">{task.particular}</td>
                                <td>{task.task_description || '-'}</td>
                                <td className="text-center">{task.weightage || '-'}</td>
                                <td>{task.deliverables || '-'}</td>
                              </tr>
                            ))
                          )}

                          {/* Total row */}
                          <tr className="table-light fw-semibold">
                            <td></td>
                            <td className="text-end pe-3">Total</td>
                            <td className="text-center">
                              {otherTasks.reduce((sum: number, t: any) => sum + (parseFloat(t.weightage) || 0), 0) || ''}
                            </td>
                            <td></td>
                          </tr>

                          <tr className="table-secondary fw-bold">
                            <td></td>
                            <td className="text-end pe-3"><span className="me-2">III</span> Grand Total</td>
                            <td className="text-center">
                              {(isMouApplicable ? (parseFloat(mouTask?.weightage) || 0) : 0) +
                                otherTasks.reduce((sum: number, t: any) => sum + (parseFloat(t.weightage) || 0), 0)}
                            </td>
                            <td></td>
                          </tr>

                        </tbody>
                      </table>
                    </div>

                    {/* Uploaded Document */}
                    {uploadedFileUrl && (
                      <div className="mt-3 mb-3">
                        <h6 className="fw-semibold mb-2">
                          <i className="fa fa-paperclip mr-1" /> Uploaded Document
                        </h6>
                        <div className="d-flex align-items-center gap-2 p-2 border rounded bg-light" style={{ maxWidth: '400px' }}>
                          <i className="fa fa-file-o text-secondary fa-lg" />
                          <span className="fw-semibold flex-grow-1">{uploadedFileName || 'Uploaded File'}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={this.openUploadedFile}
                          >
                            <i className="fa fa-external-link mr-1" />Open
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={this.downloadUploadedFile}
                          >
                            <i className="fa fa-download mr-1" />Download
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <button type="button" className="btn btn-sm cancel-button" onClick={this.goBack}>
                        Back
                      </button>
                      {canSendBack && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => this.setState({ showSendBackModal: true })}
                          disabled={showLoader}
                        >
                          <i className="fa fa-undo mr-1" /> Send Back
                        </button>
                      )}
                      {canApprove && (
                        <button type="button" className="btn btn-sm submit-button" onClick={this.handleApprove} disabled={showLoader}>
                          <i className="fa fa-check mr-1" /> Approve KPI Report
                        </button>
                      )}
                      {!canApprove && !canSendBack && kpiStatus && (
                        <span className="align-self-center text-muted">
                          {kpiStatus === 'draft' && 'Employee has not submitted the KPI report yet.'}
                          {kpiStatus === 'submitted' && role === 'reviewing' && 'Waiting for Reporting Manager approval first.'}
                          {kpiStatus === 'rm_approved' && role === 'reporting' && 'You have already approved this KPI report.'}
                          {kpiStatus === 'rvm_approved' && 'This KPI report is fully approved.'}
                          {kpiStatus === 'sent_back_by_rm' && role === 'reporting' && 'You have sent this KPI report back to the employee.'}
                          {kpiStatus === 'sent_back_by_rvm' && role === 'reviewing' && 'You have sent this KPI report back to the Reporting Manager.'}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Send Back Modal */}
                {showSendBackModal && (
                  <>
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }} onClick={() => this.setState({ showSendBackModal: false, sendBackRemarksInput: '' })} />
                    <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000, width: '500px', maxWidth: '90vw' }}>
                      <div className="modal-content" style={{ borderRadius: '8px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
                        <div className="modal-header">
                          <h5 className="modal-title">Send Back KPI Report</h5>
                          <button type="button" className="close" onClick={() => this.setState({ showSendBackModal: false, sendBackRemarksInput: '' })}>
                            <span>&times;</span>
                          </button>
                        </div>
                        <div className="modal-body">
                          <p className="text-muted">
                            {role === 'reporting'
                              ? 'This will send the KPI report back to the employee for corrections.'
                              : 'This will send the KPI report back to the Reporting Manager for re-review.'}
                          </p>
                          <div className="form-group">
                            <label className="fw-semibold">Remarks <span className="text-danger">*</span></label>
                            <textarea
                              className="form-control mt-1"
                              rows={4}
                              value={sendBackRemarksInput}
                              onChange={(e) => this.setState({ sendBackRemarksInput: e.target.value })}
                              placeholder="Please explain why you are sending back this report..."
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button type="button" className="btn btn-sm cancel-button" onClick={() => this.setState({ showSendBackModal: false, sendBackRemarksInput: '' })}>
                            Cancel
                          </button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={this.handleSendBack} disabled={showLoader || !sendBackRemarksInput.trim()}>
                            {showLoader ? 'Sending...' : 'Send Back'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(KpiApproval);
